/**
 * Utility functions for working with Facebook Pixel
 * Includes deduplication for events to prevent double firing
 */

/**
 * User identification data interface for consistent tracking across the user journey
 */
interface UserData {
  email?: string
  external_id?: string // This should be the user ID from your auth system
  customer_id?: string // Optional Stripe customer ID
  client_user_agent?: string
  client_ip_address?: string
  [key: string]: any
}

/**
 * Get standard user data for tracking from the auth context
 * @param user The authenticated user object (if available)
 * @returns Standardized user data for tracking
 */
export const getStandardUserData = (user: any): UserData => {
  if (!user) return {}

  return {
    external_id: user.id, // Primary identifier (user ID)
    email: user.email || undefined,
    customer_id: user.customerId || undefined, // Optional Stripe customer ID if available
    client_user_agent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  }
}

/**
 * Track a Facebook Pixel PageView event with deduplication
 * @param userData User identification data to align with server-side tracking
 */
export const trackPageView = (userData: UserData = {}) => {
  if (typeof window === 'undefined' || !(window as any).fbq) {
    return
  }

  // Generate a unique event ID for deduplication
  const eventId = `pageview-${Date.now()}`

  // Include user agent if not provided
  if (!userData.client_user_agent && typeof navigator !== 'undefined') {
    userData.client_user_agent = navigator.userAgent
  }

  // Track the PageView event with the unique event ID
  ;(window as any).fbq('track', 'PageView', userData, {
    eventID: eventId,
    eventSourceUrl:
      typeof window !== 'undefined' ? window.location.href : undefined,
  })

  return eventId
}

/**
 * Track a Facebook Pixel custom event with deduplication
 * @param eventName The name of the event to track
 * @param params Additional parameters to send with the event
 * @param userData User identification data to align with server-side tracking
 */
export const trackEvent = (
  eventName: string,
  params: Record<string, any> = {},
  userData: UserData = {}
) => {
  if (typeof window === 'undefined' || !(window as any).fbq) {
    return
  }

  // Generate a unique event ID for deduplication
  const eventId = `${eventName.toLowerCase()}-${Date.now()}`

  // Include user agent if not provided
  if (!userData.client_user_agent && typeof navigator !== 'undefined') {
    userData.client_user_agent = navigator.userAgent
  }

  // Add event source URL for better tracking
  const eventSourceUrl =
    typeof window !== 'undefined' ? window.location.href : undefined

  // Track the event with the unique event ID
  ;(window as any).fbq(
    'track',
    eventName,
    {
      ...params,
      ...userData,
    },
    {
      eventID: eventId,
      eventSourceUrl,
    }
  )

  return eventId
}
